import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Phone, Download, Calendar, ArrowLeft, FileText, Eye, ClipboardList, AlertTriangle, Users, Stethoscope, Clock, ExternalLink, Activity, ChevronDown, ChevronRight, Gauge, BookOpen, CheckCircle } from "lucide-react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { NextStepsSidebar } from "@/components/results/NextStepsSidebar";
import ResultsHeader from "@/components/results/ResultsHeader";
import RiskScoreGauge from "@/components/RiskScoreGauge";
import QuickActions from "@/components/QuickActions";
import TriageBasedRecommendations from "@/components/results/TriageBasedRecommendations";
import SignUpPrompt from "@/components/auth/SignUpPrompt";
import { specialistRecommendationService } from "@/services/specialistRecommendations";
import { DailySymptomTracker } from "@/components/tracking/DailySymptomTracker";
import EnhancedExportButton from "@/components/EnhancedExportButton";
import { useState, useEffect } from "react";

const ResultsNextSteps = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { answers, result, enhancedData, aiScore, confidence } = location.state || {};
  const [currentSection, setCurrentSection] = useState<string>("");
  
  // State for collapsible sections - only AmIOK Score expanded by default
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    'amiok-score': false,
    'triage-recommendations': true,
    'healthcare-contacts': true,
    'specialists': true,
    'symptom-tracking': true,
    'monitoring-areas': true,
    'resources': true,
  });

  // Extract data for enhanced specialist recommendations
  const category = answers?.symptom_constellation?.category || 'General';
  const selectedSymptoms = answers?.symptom_constellation?.selections || [];
  
  // Get AI-generated diagnoses from the enhanced data
  const aiPotentialDiagnoses = enhancedData?.constellationAnalysis?.potentialDiagnoses || [];
  
  console.log('AI diagnoses for specialist recommendations:', aiPotentialDiagnoses);

  // Get AI-powered specialist recommendations
  const aiSpecialists = aiPotentialDiagnoses.length > 0 
    ? specialistRecommendationService.getSpecialistsByAIDiagnoses(aiPotentialDiagnoses, aiScore)
    : [];

  // Fallback to traditional recommendations if no AI diagnoses
  const fallbackSpecialists = aiSpecialists.length === 0 
    ? specialistRecommendationService.getRecommendedSpecialists(
        category, 
        selectedSymptoms, 
        aiScore,
        aiPotentialDiagnoses.map(d => d.condition)
      )
    : [];

  // Combine all specialists and remove duplicates
  const allSpecialists = [...aiSpecialists, ...fallbackSpecialists]
    .filter((specialist, index, self) =>
      index === self.findIndex(s => s.specialty === specialist.specialty)
    )
    .slice(0, 4); // Limit to top 4 most relevant specialists

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'urgent': return 'border-red-200 bg-red-50/50';
      case 'prompt': return 'border-orange-200 bg-orange-50/50';
      case 'routine': return 'border-blue-200 bg-blue-50/50';
      default: return 'border-gray-200 bg-gray-50/50';
    }
  };

  const getUrgencyBadgeColor = (urgency: string) => {
    switch (urgency) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'prompt': return 'bg-orange-100 text-orange-800';
      case 'routine': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Extract primary diagnosis for intelligent recommendations
  const primaryDiagnosis = enhancedData?.diagnosticAnalysis?.potentialDiagnoses?.[0]?.condition || 
                          result?.aiExplanation || 
                          'General Health Assessment';

  const downloadReport = () => {
    const riskLevel = aiScore < 20 ? 'Very Low Risk' : aiScore < 40 ? 'Minimal Concern' : 
                     aiScore < 60 ? 'Moderate Risk' : aiScore < 80 ? 'Elevated Risk' : 'High Priority';
    
    const report = `HEALTH ASSESSMENT REPORT
Generated on: ${new Date().toLocaleDateString()}

AMIOK SCORE: ${aiScore}/100
RISK LEVEL: ${riskLevel}
CONFIDENCE: ${confidence}%

SYMPTOMS ENTERED:
${(answers?.symptom_constellation?.selections || []).map((s: string, i: number) => `${i + 1}. ${s}`).join('\n')}

SYSTEMIC SYMPTOMS:
${(answers?.systemic_symptoms || []).filter((s: string) => s !== "None of the above").map((s: string, i: number) => `${i + 1}. ${s}`).join('\n')}

RECOMMENDATIONS:
- Follow the clinical guidance provided in your assessment
- Monitor symptoms as outlined in your follow-up plan
- Contact healthcare providers as recommended
- Keep this report for your medical records

IMPORTANT CONTACTS:
Emergency: 999
NHS 111: 111
Crisis Support: 116 123

This report was generated by AmIOK AI Health Assessment.
For emergency situations, contact 999 immediately.
`;

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Health-Assessment-Report-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Scroll to section functionality
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Expand the section if it's collapsed
      if (collapsedSections[sectionId]) {
        setCollapsedSections(prev => ({ ...prev, [sectionId]: false }));
      }
      
      // Add a small delay to allow for expansion animation
      setTimeout(() => {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
        setCurrentSection(sectionId);
      }, collapsedSections[sectionId] ? 200 : 0);
    }
  };

  // Toggle section collapse
  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Update current section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'amiok-score', 'triage-recommendations', 
        'healthcare-contacts', 'specialists',
        'symptom-tracking', 'monitoring-areas', 'resources'
      ];
      
      const currentSectionId = sections.find(id => {
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (currentSectionId) {
        setCurrentSection(currentSectionId);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!aiScore) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-4">No Assessment Data Found</h2>
          <p className="text-muted-foreground mb-4">
            It looks like you haven't completed an assessment yet.
          </p>
          <Button onClick={() => navigate('/')}>
            Take Assessment
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="min-h-screen flex w-full bg-background overflow-hidden">
        {/* Sidebar Navigation */}
        <NextStepsSidebar 
          currentSection={currentSection}
          onSectionClick={scrollToSection}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Header with Sidebar Trigger */}
          <header className="bg-white/80 backdrop-blur-sm border-b border-blue-100">
            <div className="w-full px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 min-w-0">
                  <button 
                    onClick={() => navigate("/")}
                    className="flex items-center space-x-2 hover:opacity-80 transition-opacity min-w-0"
                  >
                    <div className="bg-gray-900 p-1.5 rounded-lg flex-shrink-0">
                      <Stethoscope className="h-4 w-4 text-white" />
                    </div>
                    <h1 className="text-lg font-bold text-gray-900 truncate">AmIOK</h1>
                  </button>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <EnhancedExportButton 
                    answers={answers}
                    aiScore={aiScore}
                    riskLevel={aiScore < 20 ? 'Very Low Risk' : aiScore < 40 ? 'Minimal Concern' : 
                              aiScore < 60 ? 'Moderate Risk' : aiScore < 80 ? 'Elevated Risk' : 'High Priority'}
                    confidence={confidence}
                    enhancedData={enhancedData}
                    potentialDiagnoses={enhancedData?.diagnosticAnalysis?.potentialDiagnoses?.map(d => d.condition) || []}
                    recommendations={enhancedData?.recommendations || []}
                    compact={true}
                  />
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/results/assessment', { 
                      state: { answers, result, enhancedData, aiScore, confidence } 
                    })}
                    className="text-gray-600 hover:text-gray-700 text-sm px-3 py-2 flex items-center gap-2"
                    size="sm"
                  >
                    <Eye className="h-4 w-4 flex-shrink-0" />
                    <span className="hidden sm:inline">Results</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate("/")}
                    className="text-blue-600 hover:text-blue-700 text-sm px-3 py-2"
                    size="sm"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4 flex-shrink-0" />
                    <span className="hidden sm:inline">Home</span>
                  </Button>
                </div>
              </div>
            </div>
          </header>

          {/* Main Content Area */}
          <main className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="w-full max-w-full mx-auto px-4 py-4 space-y-4">
              
              {/* AmIOK Score Section */}
              <section id="amiok-score" className="scroll-mt-16">
                <Collapsible open={!collapsedSections['amiok-score']} onOpenChange={() => toggleSection('amiok-score')}>
                  <CollapsibleTrigger className="w-full mb-3 group">
                    <div className="flex items-center justify-between py-2 hover:bg-muted/50 rounded-md px-2 transition-colors">
                      <div className="text-left">
                        <h2 className="text-lg font-bold text-foreground mb-1 flex items-center gap-2">
                          <Gauge className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          AmIOK Score
                          {collapsedSections['amiok-score'] ? 
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" /> : 
                            <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                          }
                        </h2>
                        <p className="text-xs text-muted-foreground">Your personalized health risk assessment</p>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent className="space-y-4">
                    <Card className="p-3">
                      <div className="text-center space-y-2">
                        <h3 className="text-sm font-semibold text-muted-foreground">Your AmIOK Score</h3>
                        <RiskScoreGauge
                          score={aiScore}
                          confidence={confidence}
                          size="small"
                          showDetails={false}
                          className="mx-auto"
                        />
                        <div className="text-xs text-muted-foreground">
                          <p>
                            Risk level based on your symptoms and clinical algorithms.
                          </p>
                        </div>
                      </div>
                    </Card>
                  </CollapsibleContent>
                </Collapsible>
              </section>

              {/* Triage-Based Recommendations Section */}
              <section id="triage-recommendations" className="scroll-mt-16">
                <Collapsible open={!collapsedSections['triage-recommendations']} onOpenChange={() => toggleSection('triage-recommendations')}>
                  <CollapsibleTrigger className="w-full mb-3 group">
                    <div className="flex items-center justify-between py-2 hover:bg-muted/50 rounded-md px-2 transition-colors">
                      <div className="text-left">
                        <h2 className="text-lg font-bold text-foreground mb-1 flex items-center gap-2">
                          <Stethoscope className="h-4 w-4 text-green-600 flex-shrink-0" />
                          Triage-Based Recommendations
                          {collapsedSections['triage-recommendations'] ? 
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" /> : 
                            <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                          }
                        </h2>
                        <p className="text-xs text-muted-foreground">Clinical guidance tailored to your assessment</p>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <TriageBasedRecommendations 
                      aiScore={aiScore}
                      category={answers?.symptom_constellation?.category || 'General'}
                      selectedSymptoms={answers?.symptom_constellation?.selections || []}
                      additionalSymptoms={answers?.systemic_symptoms || []}
                      potentialDiagnoses={enhancedData?.diagnosticAnalysis?.potentialDiagnoses?.map(d => d.condition) || []}
                      answers={answers}
                      medicalEvidence={enhancedData?.relevantResearch || []}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </section>

              {/* Healthcare Contacts Section */}
              <section id="healthcare-contacts" className="scroll-mt-16">
                <Collapsible open={!collapsedSections['healthcare-contacts']} onOpenChange={() => toggleSection('healthcare-contacts')}>
                  <CollapsibleTrigger className="w-full mb-3 group">
                    <div className="flex items-center justify-between py-2 hover:bg-muted/50 rounded-md px-2 transition-colors">
                      <div className="text-left">
                        <h2 className="text-lg font-bold text-foreground mb-1 flex items-center gap-2">
                          <Phone className="h-4 w-4 text-red-600 flex-shrink-0" />
                          <span className="truncate">Healthcare Contacts</span>
                          {collapsedSections['healthcare-contacts'] ? 
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" /> : 
                            <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                          }
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Contact recommendations based on your assessment
                        </p>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="space-y-3">
                      {/* Dynamic Priority Alert */}
                      {aiScore >= 60 && (
                        <Card className="p-3 border-red-200 bg-gradient-to-r from-red-50 to-red-100/50">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <div className="p-1 bg-red-100 rounded-full animate-pulse flex-shrink-0">
                                <AlertTriangle className="h-4 w-4 text-red-600" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-sm font-semibold text-red-800 truncate">Priority Medical Contacts</h3>
                                <p className="text-xs text-red-600">
                                  Urgent attention may be needed
                                </p>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <button 
                                onClick={() => window.location.href = 'tel:999'}
                                className="w-full p-2 bg-white border-2 border-red-200 rounded-lg hover:border-red-300 transition-all duration-200"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="text-left min-w-0 flex-1">
                                    <div className="flex items-center gap-1 mb-1">
                                      <Phone className="h-3 w-3 text-red-600 flex-shrink-0" />
                                      <span className="font-bold text-red-800 text-xs truncate">Emergency</span>
                                    </div>
                                    <p className="text-xs text-red-600 truncate">Life-threatening</p>
                                    <div className="text-sm font-bold text-red-600 mt-1">999</div>
                                  </div>
                                  <div className="text-red-500 ml-2 flex-shrink-0">
                                    <Phone className="h-4 w-4" />
                                  </div>
                                </div>
                              </button>
                              
                              <button 
                                onClick={() => window.location.href = 'tel:111'}
                                className="w-full p-2 bg-white border-2 border-blue-200 rounded-lg hover:border-blue-300 transition-all duration-200"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="text-left min-w-0 flex-1">
                                    <div className="flex items-center gap-1 mb-1">
                                      <Phone className="h-3 w-3 text-blue-600 flex-shrink-0" />
                                      <span className="font-bold text-blue-800 text-xs truncate">NHS 111</span>
                                    </div>
                                    <p className="text-xs text-blue-600 truncate">Non-emergency</p>
                                    <div className="text-sm font-bold text-blue-600 mt-1">111</div>
                                  </div>
                                  <div className="text-blue-500 ml-2 flex-shrink-0">
                                    <Phone className="h-4 w-4" />
                                  </div>
                                </div>
                              </button>
                            </div>
                          </div>
                        </Card>
                      )}

                      {/* Contact Cards */}
                      <div className="grid gap-3">
                        <Card className="p-3 border-red-100 hover:border-red-200 transition-all duration-200">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <div className="p-1 bg-red-100 rounded-full flex-shrink-0">
                                <AlertTriangle className="h-3 w-3 text-red-600" />
                              </div>
                              <h3 className="text-sm font-semibold text-red-700">Emergency</h3>
                            </div>
                            
                            <div className="space-y-2">
                              <button 
                                onClick={() => window.location.href = 'tel:999'}
                                className="w-full p-2 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md transition-all duration-200"
                              >
                                <div className="flex justify-between items-center">
                                  <div className="text-left min-w-0 flex-1">
                                    <div className="font-medium text-red-800 text-sm">999</div>
                                    <div className="text-xs text-red-600 truncate">Life-threatening</div>
                                  </div>
                                  <Phone className="h-3 w-3 text-red-600 flex-shrink-0" />
                                </div>
                              </button>
                              
                              <button 
                                onClick={() => window.location.href = 'tel:111'}
                                className="w-full p-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-all duration-200"
                              >
                                <div className="flex justify-between items-center">
                                  <div className="text-left min-w-0 flex-1">
                                    <div className="font-medium text-blue-800 text-sm">111</div>
                                    <div className="text-xs text-blue-600 truncate">Urgent non-emergency</div>
                                  </div>
                                  <Phone className="h-3 w-3 text-blue-600 flex-shrink-0" />
                                </div>
                              </button>
                            </div>
                          </div>
                        </Card>

                        <Card className="p-3 border-blue-100 hover:border-blue-200 transition-all duration-200">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <div className="p-1 bg-blue-100 rounded-full flex-shrink-0">
                                <Stethoscope className="h-3 w-3 text-blue-600" />
                              </div>
                              <h3 className="text-sm font-semibold text-blue-700">Primary Care</h3>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="p-2 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="text-left">
                                  <div className="font-medium text-blue-800 text-sm">GP Surgery</div>
                                  <div className="text-xs text-blue-600">Book routine appointment</div>
                                </div>
                              </div>
                              
                              <div className="p-2 bg-green-50 border border-green-200 rounded-md">
                                <div className="text-left">
                                  <div className="font-medium text-green-800 text-sm">NHS Walk-in Centre</div>
                                  <div className="text-xs text-green-600">No appointment needed</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>

                        <Card className="p-3 border-purple-100 hover:border-purple-200 transition-all duration-200">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <div className="p-1 bg-purple-100 rounded-full flex-shrink-0">
                                <Users className="h-3 w-3 text-purple-600" />
                              </div>
                              <h3 className="text-sm font-semibold text-purple-700">Mental Health Support</h3>
                            </div>
                            
                            <div className="space-y-2">
                              <button 
                                onClick={() => window.location.href = 'tel:116123'}
                                className="w-full p-2 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-md transition-all duration-200"
                              >
                                <div className="flex justify-between items-center">
                                  <div className="text-left min-w-0 flex-1">
                                    <div className="font-medium text-purple-800 text-sm">116 123</div>
                                    <div className="text-xs text-purple-600 truncate">Samaritans - 24/7</div>
                                  </div>
                                  <Phone className="h-3 w-3 text-purple-600 flex-shrink-0" />
                                </div>
                              </button>
                              
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={() => window.open('https://www.nhs.uk/mental-health/', '_blank')}
                                className="w-full p-2 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 hover:text-indigo-800 text-xs"
                              >
                                <div className="flex justify-between items-center w-full">
                                  <div className="text-left min-w-0 flex-1">
                                    <div className="font-medium text-indigo-800 text-sm">NHS Mental Health</div>
                                    <div className="text-xs text-indigo-600 truncate">Online support</div>
                                  </div>
                                  <ExternalLink className="h-3 w-3 text-indigo-600 flex-shrink-0" />
                                </div>
                              </Button>
                            </div>
                          </div>
                        </Card>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </section>

              {/* Specialist Contacts Section */}
              <section id="specialists" className="scroll-mt-16">
                <Collapsible open={!collapsedSections['specialists']} onOpenChange={() => toggleSection('specialists')}>
                  <CollapsibleTrigger className="w-full mb-3 group">
                    <div className="flex items-center justify-between py-2 hover:bg-muted/50 rounded-md px-2 transition-colors">
                      <div className="text-left">
                        <h2 className="text-lg font-bold text-foreground mb-1 flex items-center gap-2">
                          <Users className="h-4 w-4 text-purple-600 flex-shrink-0" />
                          <span className="truncate">Specialist Contacts</span>
                          {collapsedSections['specialists'] ? 
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" /> : 
                            <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                          }
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Recommended specialists based on your symptoms
                        </p>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="space-y-3">
                      {allSpecialists.length > 0 ? (
                        allSpecialists.map((specialist, index) => (
                          <Card key={index} className="p-3 border-blue-200 bg-blue-50/50">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="min-w-0 flex-1">
                                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                                    {specialist.specialty}
                                  </h3>
                                  <p className="text-xs text-gray-600 line-clamp-2">
                                    {specialist.description || 'Recommended based on your symptoms'}
                                  </p>
                                </div>
                                <div className="ml-2 flex-shrink-0">
                                  <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800">
                                    Recommended
                                  </Badge>
                                </div>
                              </div>
                              
                              <div className="text-xs text-gray-700 space-y-1">
                                <div><strong>Contact:</strong> Contact your GP for referral</div>
                                <div><strong>Expected Wait:</strong> 2-4 weeks via GP referral</div>
                              </div>
                            </div>
                          </Card>
                        ))
                      ) : (
                        <Card className="p-4 text-center">
                          <p className="text-sm text-muted-foreground">
                            Based on your assessment, general practitioner care is recommended.
                            Contact your GP for further evaluation if symptoms persist or worsen.
                          </p>
                        </Card>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </section>

              {/* Symptom Tracking Section */}
              <section id="symptom-tracking" className="scroll-mt-16">
                <Collapsible open={!collapsedSections['symptom-tracking']} onOpenChange={() => toggleSection('symptom-tracking')}>
                  <CollapsibleTrigger className="w-full mb-3 group">
                    <div className="flex items-center justify-between py-2 hover:bg-muted/50 rounded-md px-2 transition-colors">
                      <div className="text-left">
                        <h2 className="text-lg font-bold text-foreground mb-1 flex items-center gap-2">
                          <ClipboardList className="h-4 w-4 text-teal-600 flex-shrink-0" />
                          <span className="truncate">Symptom Tracking</span>
                          {collapsedSections['symptom-tracking'] ? 
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" /> : 
                            <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                          }
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Monitor your symptoms and track changes over time
                        </p>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <DailySymptomTracker aiScore={aiScore} />
                  </CollapsibleContent>
                </Collapsible>
              </section>

              {/* Monitoring Action Plan Section */}
              <section id="monitoring-areas" className="scroll-mt-16">
                <Collapsible open={!collapsedSections['monitoring-areas']} onOpenChange={() => toggleSection('monitoring-areas')}>
                  <CollapsibleTrigger className="w-full mb-3 group">
                    <div className="flex items-center justify-between py-2 hover:bg-muted/50 rounded-md px-2 transition-colors">
                      <div className="text-left">
                        <h2 className="text-lg font-bold text-foreground mb-1 flex items-center gap-2">
                          <Eye className="h-4 w-4 text-orange-600 flex-shrink-0" />
                          <span className="truncate">Monitoring Action Plan</span>
                          {collapsedSections['monitoring-areas'] ? 
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" /> : 
                            <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                          }
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Key areas to watch and track for your health management
                        </p>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="space-y-3">
                      {/* Red Flags */}
                      <Card className="p-3 border-l-4 border-l-red-400">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <AlertTriangle className="h-3 w-3 text-red-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-red-700">Red Flags</h3>
                              <p className="text-xs text-red-600">Emergency warning signs</p>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-700 leading-relaxed">
                            Watch for severe worsening of symptoms, difficulty breathing, chest pain, severe headache, or loss of consciousness. Seek immediate medical attention.
                          </div>
                          <div className="text-xs">
                            <span className="text-red-700 font-medium">Action:</span>
                            <span className="text-red-600 ml-1">Call 999 immediately</span>
                          </div>
                        </div>
                      </Card>

                      {/* Symptom Changes */}
                      <Card className="p-3 border-l-4 border-l-orange-400">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Eye className="h-3 w-3 text-orange-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-orange-700">Symptom Changes</h3>
                              <p className="text-xs text-orange-600">Monitor progression</p>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-700 leading-relaxed">
                            Track any new symptoms or significant changes in existing ones. Note patterns, triggers, and what helps or worsens symptoms.
                          </div>
                          <div className="text-xs">
                            <span className="text-orange-700 font-medium">Tool:</span>
                            <span className="text-orange-600 ml-1">Daily symptom tracker above</span>
                          </div>
                        </div>
                      </Card>

                      {/* Recovery Progress */}
                      <Card className="p-3 border-l-4 border-l-green-400">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Activity className="h-3 w-3 text-green-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-green-700">Recovery Progress</h3>
                              <p className="text-xs text-green-600">Improvement tracking</p>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-700 leading-relaxed">
                            Monitor your energy levels, sleep quality, appetite, and ability to perform daily activities. Note improvements or setbacks.
                          </div>
                          <div className="text-xs">
                            <span className="text-green-700 font-medium">Focus:</span>
                            <span className="text-green-600 ml-1">Daily functioning & quality of life</span>
                          </div>
                        </div>
                      </Card>

                      {/* Treatment Response */}
                      <Card className="p-3 border-l-4 border-l-indigo-400">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Clock className="h-3 w-3 text-indigo-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-indigo-700">Treatment Response</h3>
                              <p className="text-xs text-indigo-600">Therapy effectiveness</p>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-700 leading-relaxed">
                            Follow your prescribed treatments and monitor how your symptoms and overall health respond to care.
                          </div>
                          <div className="text-xs">
                            <span className="text-indigo-700 font-medium">Focus:</span>
                            <span className="text-indigo-600 ml-1">Symptom response & wellbeing</span>
                          </div>
                        </div>
                      </Card>

                      {/* Lifestyle Habits */}
                      <Card className="p-3 border-l-4 border-l-teal-400">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Calendar className="h-3 w-3 text-teal-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-teal-700">Lifestyle Habits</h3>
                              <p className="text-xs text-teal-600">Daily wellness factors</p>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-700 leading-relaxed">
                            Track sleep patterns, physical activity, diet, stress levels, and hydration. These can significantly impact your health and recovery.
                          </div>
                          <div className="text-xs">
                            <span className="text-teal-700 font-medium">Areas:</span>
                            <span className="text-teal-600 ml-1">Sleep, exercise, nutrition, stress</span>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </section>

              {/* Additional Resources Section */}
              <section id="resources" className="scroll-mt-16">
                <Collapsible open={!collapsedSections['resources']} onOpenChange={() => toggleSection('resources')}>
                  <CollapsibleTrigger className="w-full mb-3 group">
                    <div className="flex items-center justify-between py-2 hover:bg-muted/50 rounded-md px-2 transition-colors">
                      <div className="text-left">
                        <h2 className="text-lg font-bold text-foreground mb-1 flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                          Additional Healthcare Resources
                          {collapsedSections['resources'] ? 
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" /> : 
                            <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                          }
                        </h2>
                        <p className="text-xs text-muted-foreground">Educational materials and support services</p>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="space-y-3">
                      <Card className="p-3 border-blue-200 bg-blue-50/50">
                        <div className="space-y-3">
                          <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            NHS Health Information
                          </h3>
                          
                          <div className="space-y-2">
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => window.open('https://www.nhs.uk/conditions/', '_blank')}
                              className="w-full p-2 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 text-xs"
                            >
                              <div className="flex justify-between items-center w-full">
                                <div className="text-left min-w-0 flex-1">
                                  <div className="font-medium text-blue-800 text-sm">NHS Conditions Guide</div>
                                  <div className="text-xs text-blue-600 truncate">Comprehensive health information</div>
                                </div>
                                <ExternalLink className="h-3 w-3 text-blue-600 flex-shrink-0" />
                              </div>
                            </Button>
                            
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => window.open('https://www.nhs.uk/service-search/', '_blank')}
                              className="w-full p-2 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 text-xs"
                            >
                              <div className="flex justify-between items-center w-full">
                                <div className="text-left min-w-0 flex-1">
                                  <div className="font-medium text-blue-800 text-sm">Find NHS Services</div>
                                  <div className="text-xs text-blue-600 truncate">Locate nearby healthcare</div>
                                </div>
                                <ExternalLink className="h-3 w-3 text-blue-600 flex-shrink-0" />
                              </div>
                            </Button>
                          </div>
                        </div>
                      </Card>

                      <Card className="p-3 border-green-200 bg-green-50/50">
                        <div className="space-y-3">
                          <h3 className="text-sm font-semibold text-green-900 flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Support Services
                          </h3>
                          
                          <div className="space-y-2">
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => window.open('https://www.citizensadvice.org.uk/', '_blank')}
                              className="w-full p-2 bg-white border border-green-200 text-green-700 hover:bg-green-50 text-xs"
                            >
                              <div className="flex justify-between items-center w-full">
                                <div className="text-left min-w-0 flex-1">
                                  <div className="font-medium text-green-800 text-sm">Citizens Advice</div>
                                  <div className="text-xs text-green-600 truncate">Health & benefits guidance</div>
                                </div>
                                <ExternalLink className="h-3 w-3 text-green-600 flex-shrink-0" />
                              </div>
                            </Button>
                            
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => window.open('https://www.healthwatch.co.uk/', '_blank')}
                              className="w-full p-2 bg-white border border-green-200 text-green-700 hover:bg-green-50 text-xs"
                            >
                              <div className="flex justify-between items-center w-full">
                                <div className="text-left min-w-0 flex-1">
                                  <div className="font-medium text-green-800 text-sm">Healthwatch</div>
                                  <div className="text-xs text-green-600 truncate">Your health and care voice</div>
                                </div>
                                <ExternalLink className="h-3 w-3 text-green-600 flex-shrink-0" />
                              </div>
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </section>

              {/* Export Report Section */}
              <EnhancedExportButton 
                answers={answers}
                aiScore={aiScore}
                riskLevel={aiScore < 20 ? 'Very Low Risk' : aiScore < 40 ? 'Minimal Concern' : 
                          aiScore < 60 ? 'Moderate Risk' : aiScore < 80 ? 'Elevated Risk' : 'High Priority'}
                confidence={confidence}
                enhancedData={enhancedData}
                potentialDiagnoses={enhancedData?.diagnosticAnalysis?.potentialDiagnoses?.map(d => d.condition) || []}
                recommendations={enhancedData?.recommendations || []}
              />

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-6">
                <Button 
                  onClick={() => navigate('/results/assessment', { 
                    state: { answers, result, enhancedData, aiScore, confidence } 
                  })}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Results
                </Button>
                
                <Button 
                  onClick={() => navigate('/results/complete', { 
                    state: { answers, result, enhancedData, aiScore, confidence } 
                  })}
                  className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Complete Assessment
                </Button>
              </div>

              {/* Sign Up Prompt */}
              {!user && <SignUpPrompt />}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default ResultsNextSteps;